import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import FilterControls from './components/FilterControls';
import FeaturedCarousel from './components/FeaturedCarousel';
import ProjectGrid from './components/ProjectGrid';
import ImageLightbox from './components/ImageLightbox';
import ContactHub from '../../components/ui/ContactHub';
import ShoppingCart from '../../components/ui/ShoppingCart';


const ProjectsGallery = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    serviceType: 'all',
    location: 'all',
    dateRange: 'all',
    sortBy: 'newest'
  });
  
  const [projects, setProjects] = useState([]);
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [lightboxData, setLightboxData] = useState({
    isOpen: false,
    project: null,
    imageIndex: 0
  });

  // Mock data for projects
  const mockProjects = [
    {
      id: 1,
      title: "Complete Home Deep Cleaning Transformation",
      description: "A comprehensive deep cleaning service that transformed this family home from top to bottom, including carpet cleaning, window washing, and sanitization.",
      serviceType: "deep-cleaning",
      serviceTypeLabel: "Deep Cleaning",
      location: "Downtown",
      area: "2,500 sq ft",
      duration: "8 hours",
      completionDate: "Dec 15, 2024",
      views: 1247,
      featured: true,
      type: "before-after",
      images: [
        {
          url: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&h=600&fit=crop",
          alt: "Living room before cleaning",
          type: "before",
          description: "Living room before our deep cleaning service"
        },
        {
          url: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=800&h=600&fit=crop",
          alt: "Living room after cleaning",
          type: "after",
          description: "Sparkling clean living room after our service"
        }
      ],
      testimonial: {
        customerName: "Sarah Johnson",
        customerTitle: "Homeowner",
        customerAvatar: "https://randomuser.me/api/portraits/women/32.jpg",
        rating: 5,
        comment: "Clean360 exceeded all my expectations. My home has never looked better!"
      }
    },
    {
      id: 2,
      title: "Office Building Commercial Cleaning",
      description: "Weekly commercial cleaning service for a 10-story office building, maintaining pristine conditions for over 200 employees.",
      serviceType: "office-cleaning",
      serviceTypeLabel: "Office Cleaning",
      location: "North Side",
      area: "15,000 sq ft",
      duration: "12 hours",
      completionDate: "Dec 12, 2024",
      views: 892,
      featured: true,
      type: "before-after",
      images: [
        {
          url: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&h=600&fit=crop",
          alt: "Office space before cleaning",
          type: "before"
        },
        {
          url: "https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=800&h=600&fit=crop",
          alt: "Office space after cleaning",
          type: "after"
        }
      ],
      testimonial: {
        customerName: "Michael Chen",
        customerTitle: "Facility Manager",
        customerAvatar: "https://randomuser.me/api/portraits/men/45.jpg",
        rating: 5,
        comment: "Professional, reliable, and thorough. Our employees love the clean environment."
      }
    },
    {
      id: 3,
      title: "Luxury Carpet Restoration Project",
      description: "Professional carpet cleaning and restoration for antique Persian rugs and high-end carpeting in a luxury residence.",
      serviceType: "carpet-cleaning",
      serviceTypeLabel: "Carpet Cleaning",
      location: "Suburbs",
      area: "1,200 sq ft",
      duration: "6 hours",
      completionDate: "Dec 10, 2024",
      views: 654,
      featured: false,
      type: "before-after",
      images: [
        {
          url: "https://images.unsplash.com/photo-1586105251261-72a756497a11?w=800&h=600&fit=crop",
          alt: "Carpet before cleaning"
        },
        {
          url: "https://images.unsplash.com/photo-1586105449897-20b5efeb3233?w=800&h=600&fit=crop",
          alt: "Carpet after cleaning"
        }
      ],
      testimonial: {
        customerName: "Elizabeth Davis",
        customerTitle: "Homeowner",
        customerAvatar: "https://randomuser.me/api/portraits/women/28.jpg",
        rating: 5,
        comment: "They brought my grandmother's Persian rug back to life. Incredible work!"
      }
    },
    {
      id: 4,
      title: "Post-Construction Cleanup Excellence",
      description: "Complete post-construction cleaning for a newly built residential complex, removing all debris and preparing units for occupancy.",
      serviceType: "post-construction",
      serviceTypeLabel: "Post-Construction",
      location: "East Side",
      area: "8,000 sq ft",
      duration: "16 hours",
      completionDate: "Dec 8, 2024",
      views: 1156,
      featured: true,
      type: "before-after",
      images: [
        {
          url: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&h=600&fit=crop",
          alt: "Construction site before cleanup"
        },
        {
          url: "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&h=600&fit=crop",
          alt: "Clean space after construction cleanup"
        }
      ],
      testimonial: {
        customerName: "Robert Martinez",
        customerTitle: "Construction Manager",
        customerAvatar: "https://randomuser.me/api/portraits/men/38.jpg",
        rating: 5,
        comment: "Clean360 made our handover process seamless. Outstanding attention to detail."
      }
    },
    {
      id: 5,
      title: "Medical Facility Disinfection Service",
      description: "Comprehensive disinfection and sanitization service for a medical clinic, ensuring the highest standards of cleanliness and safety.",
      serviceType: "disinfection",
      serviceTypeLabel: "Disinfection",
      location: "West Side",
      area: "3,500 sq ft",
      duration: "10 hours",
      completionDate: "Dec 5, 2024",
      views: 743,
      featured: false,
      type: "before-after",
      images: [
        {
          url: "https://images.unsplash.com/photo-1551601651-2a8555f1a136?w=800&h=600&fit=crop",
          alt: "Medical facility before disinfection"
        },
        {
          url: "https://images.unsplash.com/photo-1551601651-bc60e9b5ae6a?w=800&h=600&fit=crop",
          alt: "Medical facility after disinfection"
        }
      ],
      testimonial: {
        customerName: "Dr. Amanda Wilson",
        customerTitle: "Medical Director",
        customerAvatar: "https://randomuser.me/api/portraits/women/42.jpg",
        rating: 5,
        comment: "Their disinfection protocols exceed healthcare standards. We trust them completely."
      }
    },
    {
      id: 6,
      title: "Luxury Vinyl Flooring Installation",
      description: "Premium vinyl flooring installation in a modern apartment, featuring waterproof planks with realistic wood grain texture.",
      serviceType: "vinyl-flooring",
      serviceTypeLabel: "Vinyl Flooring",
      location: "Downtown",
      area: "1,800 sq ft",
      duration: "14 hours",
      completionDate: "Dec 3, 2024",
      views: 892,
      featured: false,
      type: "before-after",
      images: [
        {
          url: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=800&h=600&fit=crop",
          alt: "Room before flooring installation"
        },
        {
          url: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=800&h=600&fit=crop",
          alt: "Room with new vinyl flooring"
        }
      ],
      testimonial: {
        customerName: "James Thompson",
        customerTitle: "Homeowner",
        customerAvatar: "https://randomuser.me/api/portraits/men/29.jpg",
        rating: 5,
        comment: "The vinyl flooring looks amazing and was installed perfectly. Highly recommend!"
      }
    }
  ];

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load initial data
  useEffect(() => {
    loadProjects();
  }, [filters]);

  const loadProjects = async () => {
    setLoading(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Filter and sort projects
    let filteredProjects = [...mockProjects];
    
    if (filters?.serviceType !== 'all') {
      filteredProjects = filteredProjects?.filter(p => p?.serviceType === filters?.serviceType);
    }
    
    if (filters?.location !== 'all') {
      filteredProjects = filteredProjects?.filter(p => p?.location === filters?.location);
    }
    
    // Sort projects
    switch (filters?.sortBy) {
      case 'oldest':
        filteredProjects?.sort((a, b) => new Date(a.completionDate) - new Date(b.completionDate));
        break;
      case 'popular':
        filteredProjects?.sort((a, b) => b?.views - a?.views);
        break;
      case 'service-type':
        filteredProjects?.sort((a, b) => a?.serviceTypeLabel?.localeCompare(b?.serviceTypeLabel));
        break;
      default: // newest
        filteredProjects?.sort((a, b) => new Date(b.completionDate) - new Date(a.completionDate));
    }
    
    setProjects(filteredProjects);
    setFeaturedProjects(filteredProjects?.filter(p => p?.featured));
    setCurrentPage(1);
    setHasMore(filteredProjects?.length > 6);
    setLoading(false);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      serviceType: 'all',
      location: 'all',
      dateRange: 'all',
      sortBy: 'newest'
    });
  };

  const handleLoadMore = async () => {
    // Simulate loading more projects
    await new Promise(resolve => setTimeout(resolve, 1000));
    setCurrentPage(prev => prev + 1);
    
    // For demo, stop loading after page 3
    if (currentPage >= 2) {
      setHasMore(false);
    }
  };

  const handleImageClick = (project, imageIndex = 0) => {
    setLightboxData({
      isOpen: true,
      project,
      imageIndex
    });
  };

  const handleCloseLightbox = () => {
    setLightboxData({
      isOpen: false,
      project: null,
      imageIndex: 0
    });
  };

  const handleBookService = (serviceType) => {
    // Navigate to service details or booking page
    navigate('/service-details', { state: { selectedService: serviceType } });
  };

  const handleToggleFilterPanel = () => {
    setIsFilterPanelOpen(!isFilterPanelOpen);
  };

  return (
    <>
      <Helmet>
        <title>Projects Gallery - Clean360 | Before & After Transformations</title>
        <meta name="description" content="Explore Clean360's impressive portfolio of cleaning and flooring projects. View before and after photos of our residential and commercial transformations." />
        <meta name="keywords" content="cleaning projects, before after photos, cleaning gallery, flooring installation, Clean360 portfolio" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        {/* Page Header */}
        <div className="pt-16 bg-gradient-to-br from-primary/5 to-accent/5 border-b border-border">
          <div className="max-w-7xl mx-auto px-4 lg:px-6 py-12">
            <div className="text-center">
              <h1 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
                Projects Gallery
              </h1>
              <p className="text-lg text-text-secondary max-w-3xl mx-auto">
                Discover the quality and attention to detail that sets Clean360 apart. 
                Browse our portfolio of successful cleaning and flooring transformations.
              </p>
            </div>
          </div>
        </div>

        {/* Filter Controls */}
        <FilterControls
          filters={filters}
          onFilterChange={handleFilterChange}
          resultCount={projects?.length}
          onClearFilters={handleClearFilters}
          isMobile={isMobile}
          isFilterPanelOpen={isFilterPanelOpen}
          onToggleFilterPanel={handleToggleFilterPanel}
        />

        {/* Featured Carousel */}
        {featuredProjects?.length > 0 && (
          <FeaturedCarousel
            featuredProjects={featuredProjects}
            onImageClick={handleImageClick}
            onBookService={handleBookService}
          />
        )}

        {/* Project Grid */}
        <ProjectGrid
          projects={projects}
          loading={loading}
          hasMore={hasMore}
          onLoadMore={handleLoadMore}
          onImageClick={handleImageClick}
          onBookService={handleBookService}
        />

        {/* Image Lightbox */}
        <ImageLightbox
          isOpen={lightboxData?.isOpen}
          onClose={handleCloseLightbox}
          project={lightboxData?.project}
          initialImageIndex={lightboxData?.imageIndex}
          onBookService={handleBookService}
        />

        {/* Contact Hub */}
        <ContactHub />

        {/* Shopping Cart */}
        <ShoppingCart />
      </div>
    </>
  );
};

export default ProjectsGallery;